import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { TutorDailyUsage } from '../../../types/tutorAi';
import { formatAxisDate, formatCompactCount, formatCount, formatDate } from '../../../utils/format';
import { ChartTooltip } from '../../atoms/ChartTooltip';
import { SurfaceCard } from '../../molecules/SurfaceCard';

export interface TokenTrendChartProps {
  readonly series: readonly TutorDailyUsage[];
}

/**
 * Gráfico de área do consumo diário de tokens do assistente.
 *
 * O eixo Y usa notação compacta (`413 mil`) porque o valor exato tornaria os rótulos
 * largos demais; o número cheio aparece no tooltip do ponto.
 *
 * As cores vêm de `currentColor`: o container define a cor via classe Tailwind de token
 * e os elementos do SVG a herdam, mantendo a regra de não referenciar `@ads/tokens`
 * dentro do componente e preservando a troca de tema.
 */
export function TokenTrendChart({ series }: TokenTrendChartProps) {
  const { t } = useTranslation('tutorAI');

  const hasData = series.some(point => point.tokensUsed > 0);

  return (
    <SurfaceCard title={t('student.charts.tokenTrend')}>
      <div className="p-xxs">
        {hasData ? (
          <div className="h-[17rem] w-full text-base-accent-500">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={series as TutorDailyUsage[]}
                margin={{ top: 8, right: 8, bottom: 0, left: 8 }}
              >
                <CartesianGrid
                  className="text-ctx-layout-border"
                  stroke="currentColor"
                  strokeDasharray="3 3"
                  vertical={false}
                />
                <XAxis
                  className="text-ctx-content-placeholder"
                  dataKey="date"
                  tickFormatter={formatAxisDate}
                  stroke="currentColor"
                  tick={{ fill: 'currentColor', fontSize: 11 }}
                  tickLine={false}
                  minTickGap={24}
                />
                <YAxis
                  className="text-ctx-content-placeholder"
                  stroke="currentColor"
                  tick={{ fill: 'currentColor', fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={56}
                  tickFormatter={value => formatCompactCount(Number(value))}
                />
                <Tooltip
                  content={
                    <ChartTooltip
                      seriesLabel={t('student.charts.tokenSeriesLabel')}
                      formatValue={formatCount}
                      formatLabel={formatDate}
                    />
                  }
                />
                <Area
                  type="monotone"
                  dataKey="tokensUsed"
                  stroke="currentColor"
                  strokeWidth={2}
                  fill="currentColor"
                  fillOpacity={0.12}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="py-xs text-center text-ctx-content-placeholder ts-paragraph-xs">
            {t('student.charts.tokenTrendEmpty')}
          </p>
        )}
      </div>
    </SurfaceCard>
  );
}
